/**
 * Shared visual and placement envelope for the tactical road.  Route points
 * remain the simulation centreline; this module only describes its ground
 * ribbon in the already-projected battlefield coordinate system.
 */
export interface RoadPoint {
  x: number;
  y: number;
}

function isRoadPoint(point: RoadPoint | null | undefined): point is RoadPoint {
  return !!point && Number.isFinite(point.x) && Number.isFinite(point.y);
}

/** Drop holes / non-finite coords so presentation helpers never throw on bad authoring data. */
export function sanitizeRoadPoints(route: readonly (RoadPoint | null | undefined)[] | null | undefined): RoadPoint[] {
  return (route ?? []).filter(isRoadPoint).map((point) => ({ x: point.x, y: point.y }));
}

/**
 * Rounds authored elbows for the visual road ribbon. Simulation continues to
 * use the authored control points, so this is purely a presentation helper.
 * A short quadratic shoulder gives natural bends while preserving endpoints.
 */
export function smoothRoadPoints(route: readonly RoadPoint[], steps = 4, cornerBias = 0.32): RoadPoint[] {
  const points = sanitizeRoadPoints(route);
  if (points.length < 3) return points;
  const out: RoadPoint[] = [{ ...points[0] }];
  const safeSteps = Math.max(2, Math.floor(steps));
  for (let i = 1; i < points.length - 1; i++) {
    const previous = points[i - 1];
    const corner = points[i];
    const next = points[i + 1];
    const beforeLen = Math.hypot(corner.x - previous.x, corner.y - previous.y);
    const afterLen = Math.hypot(next.x - corner.x, next.y - corner.y);
    const radius = Math.min(beforeLen, afterLen) * Math.min(0.45, Math.max(0.12, cornerBias));
    const before = { x: corner.x + (previous.x - corner.x) * (radius / Math.max(1, beforeLen)), y: corner.y + (previous.y - corner.y) * (radius / Math.max(1, beforeLen)) };
    const after = { x: corner.x + (next.x - corner.x) * (radius / Math.max(1, afterLen)), y: corner.y + (next.y - corner.y) * (radius / Math.max(1, afterLen)) };
    out.push(before);
    for (let step = 1; step < safeSteps; step++) {
      const t = step / safeSteps;
      out.push({
        x: (1 - t) * (1 - t) * before.x + 2 * (1 - t) * t * corner.x + t * t * after.x,
        y: (1 - t) * (1 - t) * before.y + 2 * (1 - t) * t * corner.y + t * t * after.y,
      });
    }
    out.push(after);
  }
  out.push({ ...points[points.length - 1] });
  return out;
}

// The close end of a route has a 22px paved surface plus a 2px shoulder.
// Consumers doing conservative placement validation should use this full
// envelope plus their footprint.
export const ROAD_MAX_HALF_WIDTH = 27;
export const ROAD_SURFACE_MAX_HALF_WIDTH = 26;
export const ROAD_MIN_HALF_WIDTH = 10;
// A compressed screen-space Y basis makes crossings read like a receding
// ground plane: distant roads are narrow and their cross-sections flatten.
// Keep the established half-depth basis used by placement regression and
// authored map coordinates; it still gives a clear oblique/isometric read
// without changing the road envelope contract.
const GROUND_Y_COMPRESSION = 0.5;

export function getRoadHalfWidth(y: number, fieldTop: number, fieldBottom: number): number {
  const depth = Math.max(0, Math.min(1, (y - fieldTop) / Math.max(1, fieldBottom - fieldTop)));
  return ROAD_MIN_HALF_WIDTH + (ROAD_SURFACE_MAX_HALF_WIDTH - ROAD_MIN_HALF_WIDTH) * depth;
}

/**
 * Project a perpendicular offset from the oblique ground plane back into
 * screen space. A screen-space normal makes the ribbon look top-down; lifting
 * the tangent into ground coordinates first gives its cross-section the same
 * flattened depth as the terrain plate.
 */
export function getRoadOffset(from: RoadPoint, to: RoadPoint, halfWidth: number): RoadPoint {
  if (!isRoadPoint(from) || !isRoadPoint(to)) return { x: 0, y: 0 };
  const gx = to.x - from.x;
  const gy = (to.y - from.y) / GROUND_Y_COMPRESSION;
  const len = Math.hypot(gx, gy) || 1;
  return {
    x: (-gy / len) * halfWidth,
    y: (gx / len) * halfWidth * GROUND_Y_COMPRESSION,
  };
}

/** Continuous left/right boundary for a route, using one averaged tangent at each join. */
export function buildRoadRibbon(
  route: readonly RoadPoint[],
  halfWidthAt: (y: number) => number,
  inset = 0,
): { left: RoadPoint[]; right: RoadPoint[] } {
  const points = sanitizeRoadPoints(route);
  const left: RoadPoint[] = [];
  const right: RoadPoint[] = [];
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    const from = points[Math.max(0, i - 1)] ?? point;
    const to = points[Math.min(points.length - 1, i + 1)] ?? point;
    const offset = getRoadOffset(from, to, Math.max(3, halfWidthAt(point.y) + inset));
    left.push({ x: point.x + offset.x, y: point.y + offset.y });
    right.push({ x: point.x - offset.x, y: point.y - offset.y });
  }
  return { left, right };
}

export function distanceToRoadCenterline(point: RoadPoint, routes: readonly RoadPoint[][]): number {
  if (!isRoadPoint(point)) return Number.POSITIVE_INFINITY;
  let best = Number.POSITIVE_INFINITY;
  for (const route of routes ?? []) {
    const points = sanitizeRoadPoints(route);
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const lengthSq = dx * dx + dy * dy || 1;
      const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSq));
      best = Math.min(best, Math.hypot(point.x - (a.x + dx * t), point.y - (a.y + dy * t)));
    }
  }
  return best;
}

export function isWithinRoadRibbon(
  point: RoadPoint,
  routes: readonly RoadPoint[][],
  extraClearance = 0,
): boolean {
  // Placement deliberately uses the widest visual edge.  It keeps pads safe
  // through joins and is independent of the screen's perspective depth.
  return distanceToRoadCenterline(point, routes) <= ROAD_MAX_HALF_WIDTH + extraClearance;
}
